/**
 * RFC ↔ proveedor map, and per-vendor saldos.
 *
 * Pacesetter's `sup` table has no RFC and no payment-terms field, so this map
 * is the only place a CFDI's RfcEmisor can be tied to a proveedor. It learns:
 * link an RFC once and the server back-fills every invoice already carrying it.
 */
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Button, Card, Table, TableBody, TableCell, TableHead,
  TableHeaderCell, TableRow, Text, TextInput, Title,
} from '@tremor/react';
import { currentUser } from '../../api/auth';
import { fetchPayablesVendors, mapVendor } from '../../api/payables';
import type { ApVendorBalance, ApVendorMap, User } from '../../api/types';
import { payablesPerms } from '../../lib/payablesPerms';
import { t } from '../../lib/i18n';
import AccessDenied from '../../components/AccessDenied';
import Header from '../../components/Header';
import ErrorRetry from '../../components/ErrorRetry';
import TableSkeleton from '../../components/TableSkeleton';
import { money } from './Payables';

interface Draft { sup_id: string; terms: string }

function PayableVendorsInner() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [vendors, setVendors] = useState<ApVendorMap[]>([]);
  const [balances, setBalances] = useState<ApVendorBalance[]>([]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { currentUser().then(setUser).catch(() => setUser(null)); }, []);

  const perms = payablesPerms(user);

  const load = () => {
    setErr(null);
    fetchPayablesVendors()
      .then((r) => { setVendors(r.vendors); setBalances(r.balances); setLoaded(true); })
      .catch((e: Error) => setErr(e.message));
  };

  useEffect(() => {
    if (perms.canView) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perms.canView]);

  if (user === undefined) return <TableSkeleton />;
  if (!user || !perms.canView) {
    return <AccessDenied user={user ?? ({ username: '', role: 'staff', sid_assigned: null } as User)} />;
  }
  if (err && !loaded) return <ErrorRetry message={err} onRetry={load} />;
  if (!loaded) return <TableSkeleton />;

  // An RFC with a saldo but no sup_id has never been linked to a proveedor.
  const unmapped = balances.filter((b) => !b.sup_id);

  const draftFor = (rfc: string): Draft => drafts[rfc] ?? { sup_id: '', terms: '0' };
  const setDraft = (rfc: string, patch: Partial<Draft>) =>
    setDrafts((d) => ({ ...d, [rfc]: { ...draftFor(rfc), ...patch } }));

  const link = async (rfc: string, emisorNombre: string) => {
    const d = draftFor(rfc);
    setErr(null);
    setMsg(null);
    try {
      const r = await mapVendor({
        rfc,
        emisor_nombre: emisorNombre,
        sup_id: d.sup_id,
        terms_days: Number(d.terms) || 0,
      });
      setMsg(`${rfc}: ${r.backfilled} ${t.payables.facturasActualizadas}`);
      load();
    } catch (e) { setErr((e as Error).message); }
  };

  return (
    <div className="space-y-4">
      <Title>{t.payables.proveedores}</Title>
      {err ? <Card><Text color="red">{err}</Text></Card> : null}
      {msg ? <Card><Text>{msg}</Text></Card> : null}

      {unmapped.length > 0 ? (
        <Card>
          <Title>{t.payables.sinVincular}</Title>
          <Text className="mt-1">{t.payables.sinVincularHint}</Text>
          <div className="overflow-x-auto mt-2">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{t.payables.rfc}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.proveedor}</TableHeaderCell>
                  <TableHeaderCell>{t.payables.saldo}</TableHeaderCell>
                  <TableHeaderCell>ID</TableHeaderCell>
                  <TableHeaderCell>{t.payables.diasCredito}</TableHeaderCell>
                  <TableHeaderCell> </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unmapped.map((b) => (
                  <TableRow key={b.rfc + b.moneda}>
                    <TableCell>{b.rfc}</TableCell>
                    <TableCell>{b.emisor_nombre}</TableCell>
                    <TableCell>{money(b.saldo, b.moneda)}</TableCell>
                    <TableCell>
                      {perms.canCapture ? (
                        <TextInput
                          aria-label={`Proveedor ${b.rfc}`}
                          value={draftFor(b.rfc).sup_id}
                          onChange={(e) => setDraft(b.rfc, { sup_id: e.target.value })}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {perms.canCapture ? (
                        <TextInput
                          aria-label={`Días ${b.rfc}`}
                          value={draftFor(b.rfc).terms}
                          onChange={(e) => setDraft(b.rfc, { terms: e.target.value })}
                        />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {perms.canCapture ? (
                        <Button
                          aria-label={`Vincular ${b.rfc}`}
                          onClick={() => link(b.rfc, b.emisor_nombre)}
                        >
                          {t.payables.vincular}
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : null}

      <Card>
        <div className="overflow-x-auto">
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>{t.payables.rfc}</TableHeaderCell>
                <TableHeaderCell>{t.payables.proveedor}</TableHeaderCell>
                <TableHeaderCell>ID</TableHeaderCell>
                <TableHeaderCell>{t.payables.diasCredito}</TableHeaderCell>
                <TableHeaderCell>{t.payables.saldo}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {vendors.map((v) => {
                const bal = balances.filter((b) => b.rfc === v.rfc);
                return (
                  <TableRow key={v.rfc}>
                    <TableCell>{v.rfc}</TableCell>
                    <TableCell>{v.sup_name || v.emisor_nombre}</TableCell>
                    <TableCell>{v.sup_id}</TableCell>
                    <TableCell>{v.terms_days}</TableCell>
                    <TableCell>
                      {bal.length === 0
                        ? '—'
                        : bal.map((b) => (
                          <div key={b.moneda}>{money(b.saldo, b.moneda)}</div>
                        ))}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

/**
 * Page shell. Every routed page in this app renders its OWN Header + <main>
 * (App.tsx has no shared authed layout), so without this the payables pages
 * had no nav bar at all — you could reach /pagar and not navigate away — and
 * sat flush against the window edges with no padding.
 */
export default function PayableVendors() {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  useEffect(() => { currentUser().then(setUser).catch(() => setUser(null)); }, []);
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  return (
    <div className="min-h-screen flex flex-col">
      <Header user={user} />
      <main className="flex-1 px-6 py-6 space-y-4 max-w-screen-2xl mx-auto w-full">
        {payablesPerms(user).canView ? <PayableVendorsInner /> : <AccessDenied user={user} />}
      </main>
    </div>
  );
}
